import { useEffect } from "react";

import { usePage } from "@inertiajs/react";

/**
 * Sends the parent's aggregated LMS platform to Usersnap as custom data
 * so feedback items are tagged with the student's learning platform.
 *
 * The `parentLmsPlatform` value is shared globally via Inertia middleware
 * (HandleInertiaRequests) and is available on every page. It aggregates
 * across all active students for the authenticated parent:
 *   - All on OEX → "oex"
 *   - All on WP → "wp"
 *   - Mixed or any "both" → "both"
 *   - No students → null
 *
 * Uses polling to wait for `window.userSnapApi` because the Usersnap SDK
 * loads asynchronously via a deferred script in the Blade template and
 * may not be available when the React layout mounts.
 */
export const useUsersnapLmsPlatform = () => {
    const { parentLmsPlatform } = usePage<{
        parentLmsPlatform?: string | null;
    }>().props;

    useEffect(() => {
        if (!parentLmsPlatform) return;

        const register = () => {
            const { userSnapApi } = window;

            if (!userSnapApi) return false;

            userSnapApi.on("open", (event: UsersnapEvent) => {
                event.api.setValue("custom", {
                    lms_platform: parentLmsPlatform
                });
            });

            return true;
        };

        // API may already be loaded (subsequent navigations)
        if (register()) return;

        // First page load: SDK still loading, poll until ready
        const interval = setInterval(() => {
            if (register()) {
                clearInterval(interval);
            }
        }, 500);

        return () => clearInterval(interval);
    }, [parentLmsPlatform]);
};
